// Biblioteca de medios para la cartelería TV (POS → Configuración → TV · Medios).
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon, Loader2, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

const rpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body)).data;

interface Medio { id: string; nombre: string; mime: string | null; imagen: string }

// Redimensiona la imagen a máx 1600px y la devuelve como data URL (jpeg/png) para no guardar archivos enormes.
function fileADataUrl(file: File): Promise<{ dataUrl: string; mime: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new window.Image();
      img.onload = () => {
        const max = 1600;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve({ dataUrl: reader.result as string, mime: file.type }); return; }
        ctx.drawImage(img, 0, 0, w, h);
        const mime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        resolve({ dataUrl: canvas.toDataURL(mime, 0.85), mime });
      };
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function TvMedios() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const fileRef = useRef<HTMLInputElement>(null);
  const [subiendo, setSubiendo] = useState(false);
  const { data: rows = [], isFetching, error } = useQuery<Medio[]>({ queryKey: ['tvMedios'], queryFn: () => rpc<Medio[]>('tv_medios') });

  const eliminar = useMutation({
    mutationFn: (id: string) => rpc('tv_medio_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Medio eliminado'); void qc.invalidateQueries({ queryKey: ['tvMedios'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setSubiendo(true);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) { toast.error(`${file.name}: no es imagen`); continue; }
        const { dataUrl, mime } = await fileADataUrl(file);
        await rpc('tv_medio_guardar', { _data: { nombre: file.name.replace(/\.[^.]+$/, ''), mime, imagen: dataUrl } });
      }
      toast.success('Imágenes subidas');
      void qc.invalidateQueries({ queryKey: ['tvMedios'] });
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSubiendo(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  if (error) return <div className="p-6 text-sm text-urgente">{getErrorMessage(error)}</div>;

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><ImageIcon className="w-5 h-5 text-primary" /> TV · Biblioteca de medios</h1>
          <p className="text-sm text-muted-foreground">Imágenes para las escenas tipo "Banner" de la cartelería. Se redimensionan a 1600px.</p>
        </div>
        <Button isLoading={subiendo} onClick={() => fileRef.current?.click()}><Upload className="w-4 h-4 mr-1" /> Subir imágenes</Button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => void onFiles(e.target.files)} />
      </div>

      <Card>
        <CardContent className="pt-4">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          {!isFetching && rows.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Sin imágenes. Sube las primeras para usarlas en escenas "Banner".</p>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {rows.map((m) => (
                <div key={m.id} className="group relative overflow-hidden rounded-lg border bg-muted/30">
                  <img src={m.imagen} alt={m.nombre} className="aspect-video w-full object-cover" />
                  <div className="flex items-center justify-between p-2">
                    <span className="truncate text-xs" title={m.nombre}>{m.nombre}</span>
                    <button className="text-red-500 hover:text-urgente" title="Eliminar" aria-label="Eliminar"
                      onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${m.nombre}"?`, { peligro: true })) eliminar.mutate(m.id); }}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
